import { z } from 'zod';

/**
 * Catalogo delle azioni scritte in `audit_log`. Il registro non ripete lo
 * storico ordinario (chi ha creato o modificato un record lo dicono già le
 * colonne `creato_da`/`modificato_da`, gli eventi sui file li tiene
 * `file_log`): qui finisce solo ciò che serve a ricostruire accessi, cambi di
 * permessi, uscite di dati ed eliminazioni.
 *
 * A DB la colonna resta `text` senza CHECK: le righe già scritte con
 * un'azione poi ritirata dal catalogo devono restare leggibili.
 */
export const AzioneAudit = z.enum([
  /* Accessi e sicurezza dell'account */
  'auth.login',
  'auth.login_fallito',
  'auth.login_codice_recupero',
  'auth.cambio_password',
  'auth.reset_richiesto',
  'auth.reset_completato',
  'auth.invito_creato',
  'auth.duefa_attivata',
  'auth.duefa_disattivata',
  'auth.duefa_reset_admin',
  'auth.codici_recupero_rigenerati',
  'auth.api_key_creata',
  'auth.api_key_revocata',

  /* Utenti e ruoli: chi dà a chi quali permessi */
  'utente.creato',
  'utente.ruolo_modificato',
  'utente.privilegi_modificati',
  'utente.disattivato',
  'utente.riattivato',
  'ruolo.creato',
  'ruolo.modificato',
  'ruolo.eliminato',

  /* Uscita di dati */
  'export.eseguito',
  'export.avanzato_scaricato',

  /* Eliminazioni */
  'cliente.eliminazione',
  'partner.eliminazione',
  'template.eliminazione',
  'progetto.eliminazione',
  'fase.eliminazione',
  'task.eliminazione',
  'file.cartella_eliminata',
  'file.slot_eliminato',

  /* Correzioni di dati chiave e cambi di stato automatici */
  'cliente.correzione_codice_fiscale',
  'partner.correzione_codice_fiscale',
  'progetto.correzione_tipologia_pratica',
  'cliente.promozione_attivo',
]);
export type AzioneAudit = z.infer<typeof AzioneAudit>;

/** Gruppi del registro, nell'ordine in cui compaiono nel filtro. */
export const CategoriaAudit = z.enum(['accessi', 'utenti', 'export', 'eliminazioni', 'correzioni']);
export type CategoriaAudit = z.infer<typeof CategoriaAudit>;

export const ETICHETTE_CATEGORIA_AUDIT: Record<CategoriaAudit, string> = {
  accessi: 'Accessi e sicurezza',
  utenti: 'Utenti e ruoli',
  export: 'Export',
  eliminazioni: 'Eliminazioni',
  correzioni: 'Correzioni',
};

/**
 * Etichetta e gruppo di ogni azione: fonte unica per la UI e per il filtro per
 * categoria lato server. Un'azione nuova si aggiunge qui e nell'enum.
 */
export const META_AZIONI_AUDIT: Record<
  AzioneAudit,
  { etichetta: string; categoria: CategoriaAudit }
> = {
  'auth.login': { etichetta: 'Accesso', categoria: 'accessi' },
  'auth.login_fallito': { etichetta: 'Accesso non riuscito', categoria: 'accessi' },
  'auth.login_codice_recupero': {
    etichetta: 'Accesso con codice di recupero',
    categoria: 'accessi',
  },
  'auth.cambio_password': { etichetta: 'Password cambiata', categoria: 'accessi' },
  'auth.reset_richiesto': { etichetta: 'Reimpostazione password richiesta', categoria: 'accessi' },
  'auth.reset_completato': { etichetta: 'Password reimpostata', categoria: 'accessi' },
  'auth.invito_creato': { etichetta: 'Invito di attivazione inviato', categoria: 'accessi' },
  'auth.duefa_attivata': { etichetta: 'Verifica in due passaggi attivata', categoria: 'accessi' },
  'auth.duefa_disattivata': {
    etichetta: 'Verifica in due passaggi disattivata',
    categoria: 'accessi',
  },
  'auth.duefa_reset_admin': {
    etichetta: 'Verifica in due passaggi reimpostata da un amministratore',
    categoria: 'accessi',
  },
  'auth.codici_recupero_rigenerati': {
    etichetta: 'Codici di recupero rigenerati',
    categoria: 'accessi',
  },
  'auth.api_key_creata': { etichetta: 'API key creata', categoria: 'accessi' },
  'auth.api_key_revocata': { etichetta: 'API key revocata', categoria: 'accessi' },

  'utente.creato': { etichetta: 'Utente creato', categoria: 'utenti' },
  'utente.ruolo_modificato': { etichetta: 'Ruolo dell’utente cambiato', categoria: 'utenti' },
  'utente.privilegi_modificati': {
    etichetta: 'Privilegi extra dell’utente cambiati',
    categoria: 'utenti',
  },
  'utente.disattivato': { etichetta: 'Utente disattivato', categoria: 'utenti' },
  'utente.riattivato': { etichetta: 'Utente riattivato', categoria: 'utenti' },
  'ruolo.creato': { etichetta: 'Ruolo creato', categoria: 'utenti' },
  'ruolo.modificato': { etichetta: 'Ruolo modificato', categoria: 'utenti' },
  'ruolo.eliminato': { etichetta: 'Ruolo eliminato', categoria: 'utenti' },

  'export.eseguito': { etichetta: 'Export CSV', categoria: 'export' },
  'export.avanzato_scaricato': { etichetta: 'Export avanzato scaricato', categoria: 'export' },

  'cliente.eliminazione': { etichetta: 'Cliente eliminato', categoria: 'eliminazioni' },
  'partner.eliminazione': { etichetta: 'Partner eliminato', categoria: 'eliminazioni' },
  'template.eliminazione': { etichetta: 'Template eliminato', categoria: 'eliminazioni' },
  'progetto.eliminazione': { etichetta: 'Progetto eliminato', categoria: 'eliminazioni' },
  'fase.eliminazione': { etichetta: 'Fase eliminata', categoria: 'eliminazioni' },
  'task.eliminazione': { etichetta: 'Task eliminato', categoria: 'eliminazioni' },
  'file.cartella_eliminata': { etichetta: 'Cartella eliminata', categoria: 'eliminazioni' },
  'file.slot_eliminato': { etichetta: 'Slot eliminato', categoria: 'eliminazioni' },

  'cliente.correzione_codice_fiscale': {
    etichetta: 'Codice fiscale del cliente corretto',
    categoria: 'correzioni',
  },
  'partner.correzione_codice_fiscale': {
    etichetta: 'Codice fiscale del partner corretto',
    categoria: 'correzioni',
  },
  'progetto.correzione_tipologia_pratica': {
    etichetta: 'Tipologia pratica corretta',
    categoria: 'correzioni',
  },
  'cliente.promozione_attivo': {
    etichetta: 'Cliente diventato Attivo',
    categoria: 'correzioni',
  },
};

/** Le azioni di una categoria: è il filtro che il server applica. */
export function azioniDellaCategoria(categoria: CategoriaAudit): AzioneAudit[] {
  return AzioneAudit.options.filter((a) => META_AZIONI_AUDIT[a].categoria === categoria);
}
